import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_URL } from '../../../../config/api';

export default function LogTicket() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        priority: '',
        description: ''
    });

    const [status, setStatus] = useState({
        loading: false,
        message: "",
        type: ""
    });

    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    // OTP State
    const [isVerified, setIsVerified] = useState(false);
    const [otpMode, setOtpMode] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpStatus, setOtpStatus] = useState({ loading: false, message: "", type: "" });

    const handleSendOTP = async () => {
        if (!formData.email) {
            setOtpStatus({ loading: false, message: "Please enter your email address first.", type: "error" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setOtpStatus({ loading: false, message: "Please enter a valid email address.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/request-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier: formData.email, context: "contact" })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setOtpMode(true);
                setOtpStatus({ loading: false, message: "Verification code sent to your email.", type: "success" });
            } else {
                setOtpStatus({ loading: false, message: data.message || "Failed to send code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Send Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length < 6) {
            setOtpStatus({ loading: false, message: "Please enter a valid 6-digit code.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email, otp, identifier: formData.email })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setIsVerified(true);
                setOtpMode(false);
                setOtpStatus({ loading: false, message: "Email verified successfully!", type: "success" });
                setTimeout(() => setOtpStatus({ loading: false, message: "", type: "" }), 3000);
            } else {
                setOtpStatus({ loading: false, message: data.message || "Invalid or expired code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Verify Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isVerified) {
            setStatus({ loading: false, message: "Please verify your email address before submitting.", type: "error" });
            return;
        }

        if (!recaptchaToken) {
            setStatus({ loading: false, message: "Please complete the reCAPTCHA verification.", type: "error" });
            return;
        }

        setStatus({ loading: true, message: "", type: "" });

        const baseUrl = API_URL;

        try {
            const res = await fetch(`${baseUrl}/ticket/sendTicket`, {
                method: "POST",
                body: JSON.stringify({
                    ...formData,
                    recaptchaToken
                }),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            const data = await res.json();

            if (res.ok) {
                setStatus({
                    loading: false,
                    message: "Ticket submitted successfully! We'll get back to you soon.",
                    type: "success"
                });

                // Reset form
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    subject: '',
                    priority: '',
                    description: ''
                });

                setRecaptchaToken("");
                setIsVerified(false);
                setOtpMode(false);
                setOtp("");

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                // Clear success message after 5 seconds
                setTimeout(() => {
                    setStatus({ loading: false, message: "", type: "" });
                }, 5000);
            } else {
                throw new Error(data.message || "Failed to submit ticket");
            }
        } catch (error) {
            console.error("Error:", error);
            setStatus({
                loading: false,
                message: "Failed to submit ticket. Please try again.",
                type: "error"
            });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ backgroundColor: '#000000e0' }}>
            {/* Subtle background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 right-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
            </div>

            <div className="w-full max-w-3xl relative z-10 pt-4 pb-12">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="inline-block p-3 bg-purple-500/10 rounded-2xl my-4">
                        <svg className="w-12 h-12 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>

                    <p className="text-gray-300 md:text-lg">Having an issue? Let us know and we'll get back to you soon</p>
                </div>

                {/* Form Card */}
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 md:p-10 shadow-2xl border border-white/10">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* First Name and Last Name Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* First Name */}
                            <div className="relative">
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                    placeholder="First Name"
                                />
                                <label
                                    htmlFor="firstName"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    First Name *
                                </label>
                            </div>

                            {/* Last Name */}
                            <div className="relative">
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-blue-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                    placeholder="Last Name"
                                />
                                <label
                                    htmlFor="lastName"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    Last Name *
                                </label>
                            </div>
                        </div>

                        {/* Email and Phone Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Email Address */}
                            <div className="relative group">
                                <div className="relative">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={status.loading || isVerified}
                                        className="peer w-full px-4 py-3 pt-6 pr-28 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                        placeholder="Email Address"
                                    />
                                    <label
                                        htmlFor="email"
                                        className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                    >
                                        Email Address *
                                    </label>

                                    {/* Action Button / Tick Mark */}
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center h-full pt-2">
                                        {formData.email && !isVerified && !otpMode && (
                                            <button
                                                type="button"
                                                onClick={handleSendOTP}
                                                disabled={otpStatus.loading}
                                                className="flex items-center justify-center w-9 h-9 bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white rounded-lg border border-purple-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Verify Email"
                                            >
                                                {otpStatus.loading ? (
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </button>
                                        )}
                                        {(isVerified || otpMode) && (
                                            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.2)] animate-in fade-in zoom-in duration-500">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {otpMode && (
                                    <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl space-y-4">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                maxLength={6}
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                                className="peer w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-xl text-white text-center text-2xl tracking-widest focus:outline-none focus:border-purple-500 transition-all"
                                                placeholder="000000"
                                            />
                                            <label className="block text-center text-xs text-gray-400 mt-2 uppercase tracking-wider">
                                                Enter 6-digit Code
                                            </label>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={handleVerifyOTP}
                                                disabled={otpStatus.loading}
                                                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50"
                                            >
                                                {otpStatus.loading ? "Verifying..." : "Verify Code"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setOtpMode(false)}
                                                className="px-4 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {otpStatus.message && !isVerified && (
                                    <p className={`mt-2 text-sm ${otpStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                        {otpStatus.message}
                                    </p>
                                )}
                            </div>

                            {/* Phone */}
                            <div className="relative">
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                    placeholder="Phone"
                                />
                                <label
                                    htmlFor="phone"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    Phone *
                                </label>
                            </div>
                        </div>

                        {/* Subject */}
                        <div className="relative">
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                required
                                disabled={status.loading}
                                className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                placeholder="Subject"
                            />
                            <label
                                htmlFor="subject"
                                className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                            >
                                Subject *
                            </label>
                        </div>

                        {/* Priority */}
                        <div className="relative">
                            <select
                                id="priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                required
                                disabled={status.loading}
                                className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 appearance-none cursor-pointer disabled:opacity-50"
                            >
                                <option value="" disabled className="bg-gray-900 text-gray-300">Select Priority</option>
                                <option value="low" className="bg-gray-900 text-white">🟢 Low</option>
                                <option value="medium" className="bg-gray-900 text-white">🟡 Medium</option>
                                <option value="high" className="bg-gray-900 text-white">🔴 High</option>
                            </select>
                            <label
                                htmlFor="priority"
                                className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-focus:text-purple-400"
                            >
                                Priority *
                            </label>
                            <svg
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        {/* Description */}
                        <div className="relative">
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                required
                                disabled={status.loading}
                                className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 resize-none disabled:opacity-50"
                                placeholder="Description"
                            />
                            <label
                                htmlFor="description"
                                className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                            >
                                Describe your issue *
                            </label>
                        </div>

                        {/* Success/Error Message */}
                        {status.message && (
                            <div className={`p-4 rounded-lg ${status.type === 'success'
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                                : 'bg-red-500/20 border border-red-500/50 text-red-400'
                                }`}>
                                <div className="flex items-center gap-2">
                                    {status.type === 'success' ? (
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    <span>{status.message}</span>
                                </div>
                            </div>
                        )}

                        {/* Captcha */}
                        <div className="flex justify-center flex-col items-center gap-4">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                                theme="dark"
                                onChange={(token) => setRecaptchaToken(token || "")}
                            />
                            {!recaptchaToken && (
                                <p className="text-gray-500 text-xs">Verification required to prevent spam</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-center pt-6">
                            <button
                                type="submit"
                                disabled={status.loading || !isVerified || !recaptchaToken}
                                className="px-12 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-[#000000e0] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {status.loading ? "Submitting..." : "Submit Ticket"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Footer Note */}
                <p className="text-center text-gray-500 text-sm mt-6">
                    Your ticket will be sent to our support team and we'll respond via email Or call us directly at <a href="tel:+61391167 215" className="text-purple-400 hover:underline">+61 391 167 215</a>.
                </p>
            </div>
        </div>
    );
}